/**
 * PdfDownloadContext — global state for background PDF generation.
 *
 * Lives at the app root so the floating progress pill persists across
 * page navigations (MyReports → other pages → etc.)
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

interface PdfJob {
  reportId: number;
  reportName: string;
  active: boolean;
  percent: number;
  startTime: number | null;
  error: string | null;
}

const EMPTY_JOB: PdfJob = {
  reportId: 0,
  reportName: "",
  active: false,
  percent: 0,
  startTime: null,
  error: null,
};

interface PdfDownloadContextValue {
  pdfJob: PdfJob;
  setPdfJob: React.Dispatch<React.SetStateAction<PdfJob>>;
  stopPolling: () => void;
  pollIntervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
}

const PdfDownloadContext = createContext<PdfDownloadContextValue | null>(null);

export function usePdfDownload() {
  const ctx = useContext(PdfDownloadContext);
  if (!ctx) throw new Error("usePdfDownload must be used within PdfDownloadProvider");
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Floating progress pill — always rendered at app root              */
/* ------------------------------------------------------------------ */
function PdfProgressPill() {
  const { pdfJob, setPdfJob, stopPolling } = usePdfDownload();
  const [, forceTick] = useState(0);

  // Re-render every 300ms while active for the time-remaining countdown
  useEffect(() => {
    if (!pdfJob.active) return;
    const id = setInterval(() => forceTick((t) => t + 1), 300);
    return () => clearInterval(id);
  }, [pdfJob.active]);

  if (!pdfJob.active && !pdfJob.error) return null;

  const elapsed = pdfJob.startTime ? Date.now() - pdfJob.startTime : 0;
  const pct = Math.max(pdfJob.percent, 1);
  const estimatedTotal = pct > 8 ? (elapsed / pct) * 100 : Math.max(elapsed * 5, 30000);
  const remainingMs = Math.max(0, estimatedTotal - elapsed);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;
  const timeLabel =
    pdfJob.percent >= 99
      ? "Almost done…"
      : `${mm > 0 ? `${mm}m ` : ""}${ss}s remaining`;

  // Circular progress SVG
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const strokeDash = circumference - (pdfJob.percent / 100) * circumference;

  if (pdfJob.error) {
    return (
      <div className="fixed bottom-5 right-5 z-[9999] w-[300px] rounded-xl border border-red-200 bg-white shadow-2xl dark:border-red-900 dark:bg-[#0d1828] p-4">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-red-600 dark:text-red-400">PDF Generation Failed</span>
          <button
            onClick={() => setPdfJob(EMPTY_JOB)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{pdfJob.error}</p>
      </div>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[9999] rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-[#FFFFFF0F] dark:bg-[#0d1828] p-3 flex items-center gap-3 min-w-[220px] max-w-[300px]">
      {/* Circular progress */}
      <div className="flex-shrink-0 relative w-[44px] h-[44px]">
        <svg width="44" height="44" className="rotate-[-90deg]">
          {/* Background circle */}
          <circle
            cx="22" cy="22" r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            className="text-gray-200 dark:text-[#FFFFFF0F]"
          />
          {/* Progress circle */}
          <circle
            cx="22" cy="22" r={radius}
            fill="none"
            stroke="#14b8a6"
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDash}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.5s ease-out" }}
          />
        </svg>
        {/* Percent label inside circle */}
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-teal-600 dark:text-teal-400">
          {Math.round(pdfJob.percent)}%
        </span>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-semibold text-gray-800 dark:text-white truncate">
          {pdfJob.reportName}
        </p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500">
          {timeLabel} · keep browsing
        </p>
      </div>

      {/* Cancel button */}
      <button
        onClick={() => {
          stopPolling();
          setPdfJob(EMPTY_JOB);
        }}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        title="Cancel"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */
export function PdfDownloadProvider({ children }: { children: React.ReactNode }) {
  const [pdfJob, setPdfJob] = useState<PdfJob>(EMPTY_JOB);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return (
    <PdfDownloadContext.Provider value={{ pdfJob, setPdfJob, stopPolling, pollIntervalRef }}>
      {children}
      <PdfProgressPill />
    </PdfDownloadContext.Provider>
  );
}

export { EMPTY_JOB };
export type { PdfJob };
